import type { ReportPdfPayload, ToolTextPayload } from './types';

/**
 * Shared client for BRANDCENTRAL's AIOS backend (app.brandctl.com) — the
 * free tools, the State-of-AI report, product updates, and unsubscribe.
 * Every endpoint has open
 * CORS and handles preflight; responses are `{ ok, error? }` envelopes.
 */
const AIOS = 'https://app.brandctl.com/api';

type Envelope = { ok?: boolean; error?: string };

const currentHref = (): string =>
  typeof window !== 'undefined' ? window.location.href : '';

/** POST JSON; throw a visitor-readable Error on any non-ok response. */
const post = async <T extends Envelope>(
  path: string,
  body: Record<string, unknown>,
  timeoutMs = 20_000,
): Promise<T> => {
  const ctrl = new AbortController();
  const timer = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    const res = await fetch(`${AIOS}${path}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
      signal: ctrl.signal,
    });
    const result = (await res.json().catch(() => ({}))) as T;
    if (!res.ok || result.ok === false) {
      throw new Error(
        result.error === 'rate_limited'
          ? 'That is a few requests in a row. Give it a minute, or email hello@brandctl.com.'
          : result.error || 'Something went wrong. Try again.',
      );
    }
    return result;
  } finally {
    clearTimeout(timer);
  }
};

/**
 * The lead shape every tool builds. `service` is `Tool: <name>`; `message` is the
 * on-screen result summary.
 */
export type ToolLeadInput = {
  service: string;
  name: string;
  email: string;
  message: string;
  consentBlog: boolean;
};

/**
 * What the visitor gets emailed (as a branded email + PDF, rendered by AIOS).
 * Every field is optional: by default the report is built from the tool's
 * existing result lines (`lead.message`).
 */
export type ToolReport = {
  /** Report heading. Defaults to the tool name. */
  title?: string;
  /** One-line verdict / score. Defaults to the first result line. */
  headline?: string;
  /** Extra Markdown appended after the results, e.g. an AI tool's output. */
  body?: string;
  /** What Samuel follows up with, in the tool's own words. */
  nextStep?: string;
  /** Optional download linked from the email + PDF. */
  resource?: { label: string; url: string };
};

// Lines in `message` written for the admin record, not the visitor's report.
const INTERNAL_LINE = /^(visitor requested|requested the)/i;

/**
 * Turn a tool's result lines into visitor-facing Markdown: the first line is
 * the headline (unless one is given), "Label:" lines become bullets, existing
 * "- " bullets stay, and a line ending in ":" becomes a bold sub-heading.
 */
export const reportFromMessage = (message: string, headline?: string) => {
  const lines = message.split('\n').map((l) => l.trim());
  const firstIdx = lines.findIndex(Boolean);
  const head = headline ?? (lines[firstIdx] ?? '').replace(/:$/, '');
  // With an explicit headline, the tool's own "… result:" title line is redundant.
  const dropFirst = !headline || (lines[firstIdx] ?? '').endsWith(':');
  const rest = (dropFirst ? lines.slice(firstIdx + 1) : lines).filter((l) => !INTERNAL_LINE.test(l));
  const md = rest
    .map((l) => (!l ? '' : l.startsWith('- ') ? l : l.endsWith(':') ? `**${l.slice(0, -1)}**` : `- ${l}`))
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim();
  return { headline: head, md };
};

/**
 * Save a free-tool lead to the AIOS `tool-lead` form and have AIOS email the
 * visitor their results (branded email + PDF) and notify Samuel. One form
 * serves every tool; the `tool` field (display name, `Tool: ` prefix
 * stripped) tells them apart. `send_report: "no"` saves + notifies only
 * (the State-of-AI report delivers its own PDF). Throws on failure.
 */
export const submitToolLead = (
  lead: ToolLeadInput,
  opts: { tool?: string; report?: ToolReport | false } = {},
) => {
  const tool = opts.tool ?? lead.service.replace(/^Tool:\s*/, '');
  const report = opts.report === false ? null : opts.report ?? {};
  const built = report ? reportFromMessage(lead.message, report.headline) : null;
  return post(
    '/forms/tool-lead',
    {
      email: lead.email.trim(),
      name: lead.name.trim(),
      tool,
      results: lead.message,
      consent_blog: lead.consentBlog ? 'yes' : 'no',
      source: 'Tools',
      send_report: report ? 'yes' : 'no',
      report_title: report ? report.title ?? tool : '',
      headline: built?.headline ?? '',
      report_md: built ? [built.md, report?.body?.trim()].filter(Boolean).join('\n\n') : '',
      next_step: report?.nextStep ?? (report ? 'Samuel reviews every one personally and will follow up.' : ''),
      resource_label: report?.resource?.label ?? '',
      resource_url: report?.resource?.url ?? '',
      _hp: '',
      _source: currentHref(),
    },
    // AIOS renders the PDF with headless Chrome before replying (3–6 s cold).
    45_000,
  );
};

/**
 * Claude proxy for the Path-B tools. Resolves `{ text }` on success and
 * `{}` on any failure, so callers keep their "pending / we'll email it"
 * fallback.
 */
export const generateToolText = async (
  payload: ToolTextPayload,
): Promise<{ text?: string }> => {
  try {
    const res = await post<Envelope & { text?: string }>('/tools/text', payload, 60_000);
    return { text: res.text };
  } catch (err) {
    console.warn('[aios] tools/text failed:', err);
    return {};
  }
};

/**
 * Render + email the State-of-AI report. Resolves the PDF bytes on success
 * and `{}` on failure — the lead is already saved by `submitToolLead`.
 */
export const generateReportPdf = async (
  payload: ReportPdfPayload,
): Promise<{ pdfBase64?: string; filename?: string }> => {
  try {
    const res = await post<Envelope & { pdfBase64?: string; filename?: string }>(
      '/tools/report-pdf',
      payload,
      45_000,
    );
    return { pdfBase64: res.pdfBase64, filename: res.filename };
  } catch (err) {
    console.warn('[aios] tools/report-pdf failed:', err);
    return {};
  }
};

/** Studio product-updates signup. `consentBlog` also subscribes to Field Notes. */
export const submitProductUpdates = (input: {
  name: string;
  email: string;
  consentBlog: boolean;
  source?: string;
}) =>
  post('/forms/product-updates', {
    email: input.email.trim(),
    name: input.name.trim(),
    consent_blog: input.consentBlog ? 'yes' : 'no',
    source: input.source ?? 'Studio',
    _hp: '',
    _source: currentHref(),
  });

/** Suppress an email on the Field Notes (`newsletter`) list. */
export const unsubscribeEmail = (email: string) =>
  post('/unsubscribe', { email: email.trim() });

/**
 * Chat "talk to a human" escalation. Goes to the main `contact-lead` form
 * (not `tool-lead`) because it's a real sales conversation: that form emails
 * the admin notification, so a person answers within 24 hours.
 */
export const submitChatEscalation = (input: { email: string; message: string }) =>
  post('/forms/contact-lead', {
    service: 'Chat Escalation',
    name: 'Chat visitor',
    email: input.email.trim(),
    message: input.message,
    consent_policy: 'yes',
    consent_terms: 'yes',
    consent_blog: 'no',
    _hp: '',
    _source: currentHref(),
  });
